import { BaseService, apiClient, ApiResponse } from './api';
import { 
  CurrencyRateDTO, 
  CreateCurrencyRateRequest, 
  TodaysCurrencyRateResponse,
  CurrencyRateStatusResponse
} from '../types/backend';

class CurrencyRateService extends BaseService<CurrencyRateDTO, CreateCurrencyRateRequest, CreateCurrencyRateRequest> {
  protected endpoint = '/currency-rates';

  /**
   * Get today's currency rate for USD (or specified currency)
   */
  async getTodaysRate(currency: string = 'USD'): Promise<TodaysCurrencyRateResponse | null> {
    try {
      const response = await apiClient.get<ApiResponse<TodaysCurrencyRateResponse>>(
        `${this.endpoint}/today`,
        {
          params: { currency }
        }
      );
      return response.data.data;
    } catch (error: any) {
      console.warn('Currency rate service error:', error.message);
      
      // If no rate is set for today, API returns 404
      if (error.response?.status === 404) {
        return null;
      }
      
      // If backend is unavailable (500, network error, etc.), return null instead of throwing
      if (!error.response || error.response.status >= 500 || error.code === 'ECONNREFUSED') {
        console.warn('Currency backend unavailable, returning null');
        return null;
      }
      
      // For other errors (400, 401, 403), let the error boundary handle it
      throw this.handleError(error);
    }
  }

  /**
   * Get the most recent (non-expired) rate for a currency.
   */
  async getLatestRate(currency: string = 'USD'): Promise<CurrencyRateDTO | null> {
    try {
      const response = await apiClient.get<ApiResponse<CurrencyRateDTO>>(
        `${this.endpoint}/latest`,
        {
          params: { currency }
        }
      );
      return response.data.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null;
      }
      throw this.handleError(error);
    }
  }

  /**
   * Get all currency rates for a given date (YYYY-MM-DD)
   */
  async getRatesForDate(dateIso: string): Promise<CurrencyRateDTO[]> {
    try {
      const response = await apiClient.get<ApiResponse<CurrencyRateDTO[]>>(
        `${this.endpoint}/date/${dateIso}`
      );
      return response.data.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Convenience: get today's rates in one call
   */
  async getTodayAllRates(): Promise<CurrencyRateDTO[]> {
    const today = new Date();
    const localDate = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    return this.getRatesForDate(localDate);
  }

  /**
   * Set today's currency rate (Admin only)
   */
  async setTodaysRate(currency: string, rate: number): Promise<CurrencyRateDTO> {
    try {
      // Use local date to avoid timezone issues
      const today = new Date();
      const localDate = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
      const response = await apiClient.post<ApiResponse<CurrencyRateDTO>>(
        this.endpoint,
        {
          currency,
          rate,
          dateSet: localDate
        }
      );
      return response.data.data;
    } catch (error: any) {
      console.error('Failed to set currency rate:', error.message);
      
      // If backend is unavailable, throw a user-friendly error
      if (!error.response || error.response.status >= 500) {
        throw new Error('Currency service is currently unavailable. Please try again later.');
      }
      
      throw this.handleError(error);
    }
  }

  /**
   * Get currency rates for a specific date range
   */
  async getRatesForDateRange(
    currency: string = 'USD',
    startDate: string,
    endDate: string
  ): Promise<CurrencyRateDTO[]> {
    try {
      const response = await apiClient.get<ApiResponse<CurrencyRateDTO[]>>(
        `${this.endpoint}/history`,
        {
          params: {
            currency,
            startDate,
            endDate
          }
        }
      );
      return response.data.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Check if today's rate is set
   */
  async isRateSetForToday(currency: string = 'USD'): Promise<boolean> {
    const rate = await this.getTodaysRate(currency);
    return rate !== null;
  }

  /**
   * Get currency rate status for admin dashboard
   */
  async getCurrencyStatus(): Promise<CurrencyRateStatusResponse> {
    try {
      const response = await apiClient.get<ApiResponse<CurrencyRateStatusResponse>>(
        `${this.endpoint}/status`
      );
      return response.data.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }
}

export const currencyRateService = new CurrencyRateService();
export default currencyRateService;
